/**
 * Types and pure helpers for the live mobile.bg listings served by the backend's listings API
 * (`backend/src/listingsApi/`), and the "deal" badge comparing each listing's asking price
 * with the median for its production year from the latest weekly price snapshot.
 */

import type { EngineSpec } from './expenses';
import type { YearPriceStats } from './pricing';

export interface LiveListing {
  url: string;
  title: string;
  priceAsStr: string;
  /** Asking price converted to BGN, like every other price in the scraped data. */
  priceBgn: number;
  year: number | null;
  mileageKm: number | null;
  powerHp: number | null;
  fuel: string | null;
  gearbox: string | null;
  location: string | null;
  /** The ad's own thumbnail on mobile.bg, if it has one. */
  imageUrl: string | null;
}

export interface LiveListingsResponse {
  brand: string;
  model: string;
  sourceUrl: string;
  fetchedAt: string;
  /** True when mobile.bg couldn't be reached and this is the last good result. */
  stale: boolean;
  listings: LiveListing[];
}

export type DealTone = 'good' | 'fair' | 'high';

export interface DealVerdict {
  /** Negative = cheaper than the year's median. */
  percentVsMarket: number;
  marketMedianBgn: number;
  sampleSize: number;
  tone: DealTone;
}

/** Fewer ads than this for a production year and its median says too little to judge a price. */
export const MIN_YEAR_SAMPLE = 5;
/** Within this many percent of the median counts as a fair price. */
export const FAIR_BAND_PERCENT = 10;

export function dealVerdict(listing: LiveListing, byYear: Map<number, YearPriceStats> | null): DealVerdict | null {
  if (!byYear || listing.year === null) {
    return null;
  }

  const stats = byYear.get(listing.year);
  if (!stats || stats.count < MIN_YEAR_SAMPLE || stats.median <= 0) {
    return null;
  }

  const percentVsMarket = ((listing.priceBgn - stats.median) / stats.median) * 100;
  const tone: DealTone =
    percentVsMarket <= -FAIR_BAND_PERCENT ? 'good' : percentVsMarket >= FAIR_BAND_PERCENT ? 'high' : 'fair';

  return { percentVsMarket, marketMedianBgn: stats.median, sampleSize: stats.count, tone };
}

export interface RankedListing {
  listing: LiveListing;
  verdict: DealVerdict | null;
}

/** Best deals first; listings that can't be judged follow, cheapest first. */
export function rankListingsByDeal(
  listings: LiveListing[],
  byYear: Map<number, YearPriceStats> | null,
): RankedListing[] {
  return listings
    .map((listing) => ({ listing, verdict: dealVerdict(listing, byYear) }))
    .sort((a, b) => {
      if (a.verdict && b.verdict) {
        return a.verdict.percentVsMarket - b.verdict.percentVsMarket;
      }
      if (a.verdict || b.verdict) {
        return a.verdict ? -1 : 1;
      }
      return a.listing.priceBgn - b.listing.priceBgn;
    });
}

export interface YearRange {
  from: number;
  /** Null while the generation is still in production. */
  to: number | null;
}

/** First to last production year across a generation's engine specs ("2006 year" / "" for ongoing). */
export function generationYearRange(
  specs: Array<Pick<EngineSpec, 'productionStartYear' | 'productionEndYear'>>,
): YearRange | null {
  const year = (text: string) => {
    const match = text.match(/\d{4}/);
    return match ? Number(match[0]) : null;
  };
  const starts = specs.map((spec) => year(spec.productionStartYear)).filter((y): y is number => y !== null);
  if (starts.length === 0) {
    return null;
  }
  const ends = specs.map((spec) => year(spec.productionEndYear));
  return { from: Math.min(...starts), to: ends.some((y) => y === null) ? null : Math.max(...(ends as number[])) };
}

/**
 * Whether an ad's production year falls in any of the ranges. Ads only carry a year, not a
 * generation, so a changeover year matches both generations; an ad without a year can't be placed.
 */
export function listingInYearRanges(listing: LiveListing, ranges: YearRange[]): boolean {
  if (ranges.length === 0) {
    return true;
  }
  const { year } = listing;
  return year !== null && ranges.some((range) => year >= range.from && (range.to === null || year <= range.to));
}
